import React, { useState } from 'react';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import Tabs from 'components/common/tabsTable/TabsTable';
import {
  TWILIO_INTEGRATION_TABS,
  TWILIO_INTEGRATION_TABS_VALUES,
} from 'constants/twilio/twilioIntegrationTabs';
import { CreateTwilioBrandOptions } from 'types/twilio/twilioBrand';
import { CampaignCreationFormOptions } from 'types/twilio/twilioCampaign';
import { CreateTwilioCustomerProfileOptions } from 'types/twilio/twilioCustomerProfile';
import styles from './TwilioIntegration.module.scss';
import { TwilioBrandAndCampaign } from './brandAndCampaign/TwilioBrandAndCampaign';
import { TwilioCampaignPhoneNumber } from './campaignPhoneNumber/TwilioCampaignPhoneNumber';
import { TwilioCustomerProfile } from './customerProfile/TwilioCustomerProfile';
import { useCreateTwilioBrand } from './mutations/useCreateTwilioBrand';
import { useCreateTwilioCampaign } from './mutations/useCreateTwilioCampaign';
import { useCreateTwilioCustomerProfile } from './mutations/useCreateTwilioCustomerProfile';
import {
  useGetTwilioIntegration,
  useInvalidateTwilioIntegration,
} from './queries/useGetTwilioIntegration';

interface TwilioIntegrationProps {
  businessId: number;
  businessName: string;
  businessWebsiteUrl: string;
}

/**
 * A React functional component acts as an entry point for Twilio integration.
 * This component fetches necessary data related to the provided business ID and renders
 * a page that includes multiple tabs.
 */
export const TwilioIntegration = ({
  businessId,
  businessName,
  businessWebsiteUrl,
}: TwilioIntegrationProps) => {
  const [selectedTab, setSelectedTab] = useState<TWILIO_INTEGRATION_TABS_VALUES>(
    TWILIO_INTEGRATION_TABS_VALUES.CUSTOMER_PROFILE,
  );
  const { data: twilioIntegration, isLoading: isTwilioIntegrationDetailsLoading } =
    useGetTwilioIntegration(businessId);

  const {
    mutateAsync: createTwilioCustomerProfile,
    isLoading: isCreatingTwilioCustomerProfileLoading,
  } = useCreateTwilioCustomerProfile({
    successCallback: useInvalidateTwilioIntegration(businessId),
  });
  const { mutateAsync: createTwilioBrand, isLoading: isCreatingTwilioBrandLoading } =
    useCreateTwilioBrand({
      successCallback: useInvalidateTwilioIntegration(businessId),
    });
  const { mutateAsync: createTwilioCampaign, isLoading: isCreatingTwilioCampaignLoading } =
    useCreateTwilioCampaign({
      successCallback: useInvalidateTwilioIntegration(businessId),
    });

  const handleCreateCustomerProfileClickEvent = async (
    opts: CreateTwilioCustomerProfileOptions,
  ) => {
    await createTwilioCustomerProfile({
      businessId,
      data: opts,
    });
  };

  const handleBrandCreationEvent = async (opts: CreateTwilioBrandOptions) => {
    await createTwilioBrand({
      businessId,
      data: opts,
    });
  };

  const handleCampaignCreationEvent = async (opts: CampaignCreationFormOptions) => {
    const brandRegistrationSid = twilioIntegration?.details?.twilioBrandRegistrationId;

    if (brandRegistrationSid) {
      await createTwilioCampaign({
        businessId,
        data: {
          ...opts,
          brandRegistrationSid,
        },
      });
    }
  };

  return (
    <div className={styles.tabsContainer}>
      <Tabs
        tabsData={TWILIO_INTEGRATION_TABS}
        tabValueSelected={selectedTab}
        handleTabChange={(_, newValue: TWILIO_INTEGRATION_TABS_VALUES) => {
          setSelectedTab(newValue);
        }}
        orientation="vertical"
      />

      {selectedTab === TWILIO_INTEGRATION_TABS_VALUES.CUSTOMER_PROFILE &&
        (isTwilioIntegrationDetailsLoading || isCreatingTwilioCustomerProfileLoading ? (
          <CentsLoader />
        ) : (
          <TwilioCustomerProfile
            twilioCustomerProfileId={twilioIntegration?.details?.twilioCustomerProfileId}
            businessId={businessId}
            businessName={businessName}
            businessWebsiteUrl={businessWebsiteUrl}
            savedCampaignProfile={twilioIntegration?.details?.twilioCampaignProfile}
            handleCreateCustomerProfileClickEvent={handleCreateCustomerProfileClickEvent}
          />
        ))}

      {selectedTab === TWILIO_INTEGRATION_TABS_VALUES.BRAND_AND_CAMPAIGN &&
        (isTwilioIntegrationDetailsLoading ||
        isCreatingTwilioBrandLoading ||
        isCreatingTwilioCampaignLoading ? (
          <CentsLoader />
        ) : !!twilioIntegration?.details?.twilioCustomerProfileId ? (
          <TwilioBrandAndCampaign
            brandId={twilioIntegration?.details?.twilioBrandRegistrationId}
            campaignId={twilioIntegration?.details?.twilioCampaignId}
            messagingServiceId={twilioIntegration?.details?.twilioMessagingServiceId}
            businessId={businessId}
            businessName={businessName}
            businessWebsiteUrl={businessWebsiteUrl}
            savedCampaignProfile={twilioIntegration?.details?.twilioCampaignProfile}
            handleBrandCreationEvent={handleBrandCreationEvent}
            handleCampaignCreationEvent={handleCampaignCreationEvent}
          />
        ) : (
          <p className="mt-2 ml-3">Customer profile have not been created yet</p>
        ))}

      {selectedTab === TWILIO_INTEGRATION_TABS_VALUES.PHONE_NUMBERS &&
        (isTwilioIntegrationDetailsLoading || isCreatingTwilioCampaignLoading ? (
          <CentsLoader />
        ) : (
          <TwilioCampaignPhoneNumber
            businessId={businessId}
            campaignId={twilioIntegration?.details?.twilioCampaignId}
            messagingServiceId={twilioIntegration?.details?.twilioMessagingServiceId}
          />
        ))}
    </div>
  );
};
